import { useEffect, useState } from "react";
import { api } from "../../utils/api.js";
import { parseUTC } from "../../utils/date.js";

interface AuditLogEntry {
  id: number;
  action: string;
  entityType: string;
  details: string | null;
  createdAt: string;
  user: { id: number; displayName: string; email: string } | null;
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  created: { label: "Created", color: "bg-green-100 text-green-800" },
  updated: { label: "Updated", color: "bg-blue-100 text-blue-800" },
  deleted: { label: "Deleted", color: "bg-red-100 text-red-800" },
  status_published: { label: "Published", color: "bg-green-100 text-green-800" },
  status_closed: { label: "Closed", color: "bg-red-100 text-red-800" },
  status_draft: { label: "Draft", color: "bg-gray-100 text-gray-600" },
};

function formatAction(action: string): { label: string; color: string } {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  if (action.startsWith("status_")) {
    return {
      label: action.replace("status_", "").replace(/_/g, " "),
      color: "bg-gray-100 text-gray-600",
    };
  }
  return { label: action, color: "bg-gray-100 text-gray-600" };
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - parseUTC(isoString).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return parseUTC(isoString).toLocaleDateString();
}

export function JobAuditLog({
  slug,
  open,
  onClose,
}: {
  slug: string;
  open: boolean;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get<{ results: AuditLogEntry[] }>(`/job-admin/${slug}/audit-log`)
      .then((d) => setEntries(d.results))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex">
      <div className="w-80 border-l border-gray-200 bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Activity Log</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="text-sm text-gray-400 animate-pulse">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-gray-400">No activity recorded yet.</div>
          ) : (
            <ol className="relative border-l border-gray-200">
              {[...entries].reverse().map((entry) => {
                const { label, color } = formatAction(entry.action);
                return (
                  <li key={entry.id} className="mb-4 ml-4">
                    <div className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border border-white bg-gray-300" />
                    <div className="flex items-baseline gap-2">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${color}`}
                      >
                        {label}
                      </span>
                      <time className="text-[10px] text-gray-400" title={entry.createdAt}>
                        {relativeTime(entry.createdAt)}
                      </time>
                    </div>
                    {entry.user && (
                      <p className="mt-0.5 text-xs text-gray-600">{entry.user.displayName}</p>
                    )}
                    {entry.details && <DetailsLine details={entry.details} />}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailsLine({ details }: { details: string }) {
  try {
    const parsed = JSON.parse(details) as Record<string, unknown>;
    if (parsed.fields) {
      return (
        <p className="mt-0.5 text-xs text-gray-500">
          Fields: {(parsed.fields as string[]).join(", ")}
        </p>
      );
    }
    if (parsed.candidateCount !== undefined) {
      return (
        <p className="mt-0.5 text-xs text-gray-500">{String(parsed.candidateCount)} candidates</p>
      );
    }
  } catch {
    // not JSON
  }
  return <p className="mt-0.5 text-xs text-gray-500">{details}</p>;
}
